// ABOUTME: External transcript store (SPEC-26 D4) — one source of truth for live WS transcript
// ABOUTME: messages, exposing per-sender snapshots via useSyncExternalStore so subscribers don't cross-render.

import { useSyncExternalStore } from 'react';

let latest = null; // most recent message from either sender — mirrors the legacy combined subtitle box
let bySender = { user: null, agent: null }; // most recent message per sender
const listeners = new Set();

function emit() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getLatestSnapshot() {
  return latest;
}

// Returning `bySender[sender]` directly (not a derived/spread object) is what keeps the reference
// stable across a push from the OTHER sender — bySender itself gets a new container on every push,
// but an untouched sender's own entry keeps its old object identity, so useSyncExternalStore's
// Object.is comparison bails that subscriber out of re-rendering.
function getSenderSnapshot(sender) {
  return bySender[sender];
}

/** Push a transcript message ({ sender, text, isPartial? }) into the store. */
function push({ sender, text, isPartial = false }) {
  const message = { sender, text, isPartial, ts: Date.now() };
  latest = message;
  bySender = { ...bySender, [sender]: message };
  emit();
}

/**
 * Read the current message without subscribing — one sender's with an argument, the overall latest
 * with none.
 *
 * For imperative callers that need to know what is on screen RIGHT NOW rather than render it — the
 * manual STOP button settles the agent's in-flight partial (`useManualStop`), which it can only do
 * if it can read the partial first. Hooks must keep using `useTranscriptBySender`; this returns the
 * live object and will not re-render anybody when it changes.
 *
 * The no-argument form is not a convenience: `bySender` is per-sender, so the agent's entry survives
 * whole turns and cannot say WHEN it was said. `latest` is the only ordering the store keeps, and
 * `peek('agent') === peek()` is how a caller asks "is the agent's line still the newest thing on
 * screen" — which is what stops a stop from settling a partial left over from an earlier turn.
 */
function peek(sender) {
  if (sender === undefined) return latest;
  return bySender[sender] || null;
}

/** Test-only: clear all state back to empty. */
function reset() {
  latest = null;
  bySender = { user: null, agent: null };
  emit();
}

export const transcriptStore = { push, peek, reset };

/** The single most recent message from either sender. */
export function useLatestTranscript() {
  return useSyncExternalStore(subscribe, getLatestSnapshot);
}

/** The most recent message from one sender only ('user' | 'agent'). Does not re-render when the
 * other sender pushes a new message. */
export function useTranscriptBySender(sender) {
  return useSyncExternalStore(subscribe, () => getSenderSnapshot(sender));
}

/**
 * True once either sender has produced any non-empty text this session.
 *
 * Exists so the shared CC bar (SPEC-26 Phase 4) can keep its frosted background off screen until
 * there is something to frame — before the first word, an empty pill would sit over the intro film.
 * A boolean snapshot rather than the messages themselves on purpose: the bar re-renders only when
 * this flips, so the two caption leaves keep their independent subscriptions (D4) and a streaming
 * partial still re-renders one leaf, not the whole bar. Per AC-3b the pair never reverts to empty
 * once it has spoken, so in practice this latches true exactly once.
 */
export function useHasAnyTranscript() {
  return useSyncExternalStore(subscribe, () => Boolean(bySender.user?.text || bySender.agent?.text));
}

/**
 * SPEC-26 D6: true while a turn is actively streaming in (the most recent message is a partial),
 * false once it settles. Scoped deliberately narrow: `latest`/`bySender` never revert to empty
 * after the first utterance (AC-3b: the CC pair persists showing the last line in every player
 * state, by design, already shipped in Phase 1 — not something this selector should change), so
 * "is there live text on screen" can't be the predicate without permanently latching true after
 * the first line of the whole session. `isPartial` is the one signal that actually toggles.
 *
 * This intentionally does NOT try to keep the editorial caption suppressed for as long as the CC
 * pair's last line is still visible after a turn settles — the two boxes occupy different screen
 * positions (the CC pair in the footer row, the editorial caption at bottom ~9%) and letting the
 * editorial caption resume promptly once speech pauses matches the "collision" D6 actually names:
 * both systems updating at the same instant, not both merely being present on screen at once.
 * Revisit if Phase 3/4 UX review calls for a longer cool-down tied to the CC pair's own content.
 */
export function useCaptionVisible() {
  return useSyncExternalStore(subscribe, () => {
    const isStreaming = latest?.isPartial === true;
    return isStreaming;
  });
}
